import { MATRIX_ACTION_TYPES } from '../actions/types';
import { upsertMatrixRoomsByRoomId, appendMatrixRooms } from '../utils/chatUtil';
import { computePaginationAfterRefreshMerge } from '../../views/Pages/Message/hooks/matrixRoomsPagination';

const emptyPagination = { page: 0, total: 0, hasMore: false };

const initialState = {
  rooms: [],
  loading: false,
  roomsLoadingMore: false,
  roomsPagination: { ...emptyPagination },
  error: null,
  message: null,
  // When FETCH_MATRIX_ROOMS_REQUEST was last dispatched — used to clear stuck loading.
  roomsFetchRequestedAt: 0,
  // Prevent refetching rooms on route remounts during a single SPA session.
  // This is set to true after the first successful rooms fetch (even if empty).
  hasRoomsLoaded: false,
  // Timestamp (ms) of the last successful fetch.
  roomsFetchedAt: 0,

  // All rooms (New Message page) — same shape as rooms, separate cache.
  allRooms: [],
  allRoomsLoading: false,
  allRoomsLoadingMore: false,
  allRoomsPagination: { ...emptyPagination },
  allRoomsError: null,
  allRoomsMessage: null,
  allRoomsFetchRequestedAt: 0,
  hasAllRoomsLoaded: false,
  allRoomsFetchedAt: 0,

  // Cache of room info keyed by destination user id.
  // Shape: { [userId]: { loading: boolean, error: string|null, data: any|null, fetchedAt: number } }
  roomInfoByUserId: {},

  restrictedWords: {
    loading: false,
    error: null,
    data: null,
    fetchedAt: 0,
  },
};

function resolveRoomsPayload(state, action, roomsKey) {
  const incoming = action.payload.rooms || { rooms: [] };
  const existing = state[roomsKey];

  if (action.payload.refreshMerge) {
    return upsertMatrixRoomsByRoomId(existing, incoming);
  }
  if (action.payload.append) {
    return appendMatrixRooms(existing, incoming);
  }
  return incoming;
}

export default (state = initialState, action) => {
  switch (action.type) {
    case MATRIX_ACTION_TYPES.FETCH_MATRIX_ROOMS_REQUEST:
      return {
        ...state,
        loading: action.payload?.append ? state.loading : true,
        roomsLoadingMore: Boolean(action.payload?.append),
        error: null,
        roomsFetchRequestedAt: Date.now(),
      };

    case MATRIX_ACTION_TYPES.FETCH_MATRIX_ROOMS_SUCCESS: {
      const resolvedRooms = resolveRoomsPayload(state, action, 'rooms');
      const nextPagination = action.payload.refreshMerge
        ? computePaginationAfterRefreshMerge(
          state.roomsPagination,
          action.payload.pagination,
          resolvedRooms,
        )
        : (action.payload.pagination || state.roomsPagination);

      return {
        ...state,
        loading: false,
        roomsLoadingMore: false,
        rooms: resolvedRooms,
        roomsPagination: nextPagination,
        message: action.payload.message,
        error: null,
        roomsFetchRequestedAt: 0,
        hasRoomsLoaded: true,
        roomsFetchedAt: action.payload.fetchedAt || Date.now(),
      };
    }

    case MATRIX_ACTION_TYPES.FETCH_MATRIX_ROOMS_FAILURE:
      return {
        ...state,
        loading: false,
        roomsLoadingMore: false,
        roomsFetchRequestedAt: 0,
        error: action.payload.error,
        message: null,
      };

    case MATRIX_ACTION_TYPES.FETCH_MATRIX_ROOMS_STALE_ABORT:
      if (!state.loading && !state.roomsLoadingMore) return state;
      return {
        ...state,
        loading: false,
        roomsLoadingMore: false,
        roomsFetchRequestedAt: 0,
      };

    case MATRIX_ACTION_TYPES.FETCH_MATRIX_ALL_ROOMS_REQUEST:
      return {
        ...state,
        allRoomsLoading: action.payload?.append ? state.allRoomsLoading : true,
        allRoomsLoadingMore: Boolean(action.payload?.append),
        allRoomsError: null,
        allRoomsFetchRequestedAt: Date.now(),
      };

    case MATRIX_ACTION_TYPES.FETCH_MATRIX_ALL_ROOMS_SUCCESS: {
      const resolvedRooms = resolveRoomsPayload(state, action, 'allRooms');
      const nextPagination = action.payload.refreshMerge
        ? computePaginationAfterRefreshMerge(
          state.allRoomsPagination,
          action.payload.pagination,
          resolvedRooms,
        )
        : (action.payload.pagination || state.allRoomsPagination);

      return {
        ...state,
        allRoomsLoading: false,
        allRoomsLoadingMore: false,
        allRooms: resolvedRooms,
        allRoomsPagination: nextPagination,
        allRoomsMessage: action.payload.message,
        allRoomsError: null,
        allRoomsFetchRequestedAt: 0,
        hasAllRoomsLoaded: true,
        allRoomsFetchedAt: action.payload.fetchedAt || Date.now(),
      };
    }

    case MATRIX_ACTION_TYPES.FETCH_MATRIX_ALL_ROOMS_FAILURE:
      return {
        ...state,
        allRoomsLoading: false,
        allRoomsLoadingMore: false,
        allRoomsFetchRequestedAt: 0,
        allRoomsError: action.payload.error,
        allRoomsMessage: null,
      };

    case MATRIX_ACTION_TYPES.FETCH_MATRIX_ALL_ROOMS_STALE_ABORT:
      if (!state.allRoomsLoading && !state.allRoomsLoadingMore) return state;
      return {
        ...state,
        allRoomsLoading: false,
        allRoomsLoadingMore: false,
        allRoomsFetchRequestedAt: 0,
      };

    case MATRIX_ACTION_TYPES.FETCH_MATRIX_ROOM_INFO_REQUEST: {
      const userId = action.payload?.userId;
      if (!userId) return state;
      const existingEntry = state.roomInfoByUserId?.[userId];
      return {
        ...state,
        roomInfoByUserId: {
          ...(state.roomInfoByUserId || {}),
          [userId]: {
            loading: true,
            error: null,
            data: existingEntry?.data ?? null,
            fetchedAt: existingEntry?.fetchedAt || 0,
          },
        },
      };
    }

    case MATRIX_ACTION_TYPES.FETCH_MATRIX_ROOM_INFO_SUCCESS: {
      const userId = action.payload?.userId;
      if (!userId) return state;
      return {
        ...state,
        roomInfoByUserId: {
          ...(state.roomInfoByUserId || {}),
          [userId]: {
            loading: false,
            error: null,
            data: action.payload?.data ?? null,
            fetchedAt: action.payload?.fetchedAt || Date.now(),
          },
        },
      };
    }

    case MATRIX_ACTION_TYPES.FETCH_MATRIX_ROOM_INFO_FAILURE: {
      const userId = action.payload?.userId;
      if (!userId) return state;
      const existingEntry = state.roomInfoByUserId?.[userId];
      return {
        ...state,
        roomInfoByUserId: {
          ...(state.roomInfoByUserId || {}),
          [userId]: {
            loading: false,
            error: action.payload?.error || 'Failed to fetch Matrix room info',
            data: existingEntry?.data ?? null,
            fetchedAt: existingEntry?.fetchedAt || 0,
          },
        },
      };
    }

    case MATRIX_ACTION_TYPES.FETCH_RESTRICTED_WORDS_REQUEST:
      return {
        ...state,
        restrictedWords: {
          ...(state.restrictedWords || {}),
          loading: true,
          error: null,
        },
      };

    case MATRIX_ACTION_TYPES.FETCH_RESTRICTED_WORDS_SUCCESS:
      return {
        ...state,
        restrictedWords: {
          loading: false,
          error: null,
          data: action.payload?.data ?? null,
          fetchedAt: action.payload?.fetchedAt || Date.now(),
        },
      };

    case MATRIX_ACTION_TYPES.FETCH_RESTRICTED_WORDS_FAILURE:
      return {
        ...state,
        restrictedWords: {
          ...(state.restrictedWords || {}),
          loading: false,
          error: action.payload?.error || 'Failed to fetch restricted words',
          data: null,
        },
      };

    case MATRIX_ACTION_TYPES.CLEAR_MATRIX_ERRORS:
      return {
        ...state,
        error: null,
        message: null,
        allRoomsError: null,
        allRoomsMessage: null,
        restrictedWords: { ...(state.restrictedWords || {}), error: null },
        roomInfoByUserId: Object.fromEntries(
          Object.entries(state.roomInfoByUserId || {}).map(([userId, entry]) => [
            userId,
            { ...(entry || {}), error: null },
          ])
        ),
      };

    default:
      return state;
  }
};
